export type SchemaNode = Record<string, unknown>

export type Values = Record<string, unknown>

export const isRecord = (value: unknown): value is SchemaNode => typeof value === 'object' && value !== null && !Array.isArray(value)

export const presentString = (value: unknown): string | null => (typeof value === 'string' && value.trim().length > 0 ? value.trim() : null)

export const titleCase = (name: string): string =>
  name
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

export const isBlankValue = (value: unknown): boolean =>
  value === undefined || value === null || (typeof value === 'string' && value.trim().length === 0) || (Array.isArray(value) && value.length === 0)

const withoutBlanksIn = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(withoutBlanksIn)
  }
  return isRecord(value) ? withoutBlanks(value) : value
}

export const withoutBlanks = (values: Values): Values =>
  Object.fromEntries(
    Object.entries(values)
      .filter(([, value]) => !isBlankValue(value))
      .map(([name, value]) => [name, withoutBlanksIn(value)]),
  )

export const mergePrefilled = (existing: Values, filled: Values | undefined): Values => {
  if (!filled || Object.keys(filled).length === 0) {
    return existing
  }
  const answers = Object.entries(filled).filter(([name]) => {
    const entered = existing[name]
    return isBlankValue(entered) || (isRecord(entered) && Object.keys(entered).length === 0)
  })
  return answers.length === 0 ? existing : { ...existing, ...Object.fromEntries(answers) }
}

interface SchemaSlice {
  include?: string[]
  exclude?: string[]
}

export const schemaProperties = (schema: SchemaNode): SchemaNode => (isRecord(schema.properties) ? schema.properties : {})

export const hasProperties = (schema: SchemaNode): boolean => Object.keys(schemaProperties(schema)).length > 0

const sliceOwns = (schema: SchemaNode, slice: SchemaSlice): string[] => {
  const names = Object.keys(schemaProperties(schema))
  if (slice.include) {
    return names.filter((name) => slice.include?.includes(name))
  }
  if (slice.exclude) {
    return names.filter((name) => !slice.exclude?.includes(name))
  }
  return names
}

export const sliceSchema = (schema: SchemaNode, slice: SchemaSlice): SchemaNode => {
  const properties = schemaProperties(schema)
  const owned = sliceOwns(schema, slice)
  const required = Array.isArray(schema.required) ? (schema.required as string[]) : []
  const pick = (names: string[]): SchemaNode => Object.fromEntries(Object.entries(properties).filter(([name]) => names.includes(name)))

  if (!slice.include && !slice.exclude) {
    return schema
  }
  if (slice.include) {
    return owned.length === 0 ? {} : { type: 'object', properties: pick(owned), required: required.filter((name) => owned.includes(name)) }
  }
  return { ...schema, properties: pick(owned), required: required.filter((name) => owned.includes(name)) }
}

export const applyPanelValues = (values: Values, formData: Values | undefined, schema: SchemaNode, slice: SchemaSlice): Values => {
  const owned = sliceOwns(schema, slice)
  const owns = (name: string): boolean => owned.includes(name)
  return Object.fromEntries([...Object.entries(values).filter(([name]) => !owns(name)), ...Object.entries(formData ?? {}).filter(([name]) => owns(name))])
}
